import { type Ref } from 'vue'
import { suggestState } from '@/composables/useGeo'

interface UsePostcodeStateOptions {
  /** Only auto-select if state is empty (default: false) */
  onlyWhenEmpty?: boolean
  /** Reactive reference to country code (default: 'DE') */
  countryRef?: Ref<string | undefined>
  /** Reactive reference to city (optional, improves suggestion accuracy) */
  cityRef?: Ref<string | undefined>
  /** Use ISO 3166-2 subdivision code (e.g., "DE-BY") instead of name (e.g., "Bayern") */
  useCode?: boolean
}

/**
 * Composable for automatically selecting state/subdivision based on postcode via API
 *
 * @param stateRef - Reactive reference to the state value (will be auto-updated)
 * @param options - Configuration options
 */
export function usePostcodeState(
  stateRef: Ref<string | undefined>,
  options: UsePostcodeStateOptions = {}
) {
  const { onlyWhenEmpty = false, countryRef, cityRef, useCode = false } = options

  /**
   * Fetch state suggestion from backend API based on postcode
   * Call this on postcode field blur
   */
  const fetchStateFromPostcode = async (postcode: string) => {
    // Skip if state already set and onlyWhenEmpty is true
    if (onlyWhenEmpty && stateRef.value) return

    // Get country code (default to Germany)
    const country = countryRef?.value?.toUpperCase() || 'DE'
    const city = cityRef?.value

    // Basic validation - need at least some postcode input
    if (!postcode || postcode.trim().length < 2) return

    try {
      const suggestion = await suggestState(country, postcode.trim(), city)

      if (suggestion) {
        // Use code (e.g., "DE-BY") or name (e.g., "Bayern") based on option
        stateRef.value = useCode ? suggestion.code : suggestion.name
      }
    } catch {
      // Silently fail - suggestion is an optional enhancement
    }
  }

  return {
    fetchStateFromPostcode
  }
}
